/**
 * neighborWalk — spatial helpers for keyboard navigation in the workspace
 * (doc 147 §5.3 redesign · finishes the keyboard map's walk-neighbour and
 * focus-domain actions).
 *
 * Pure functions over the manifest + a layout map; the App.tsx dispatcher
 * calls these to translate a key press into the next focal entity id.
 */

import type { AtlasManifest } from '@atlas-manifest/types';
import type { LayoutCoords } from '../graph/buildGraph.js';
import {
  entityById,
  outgoingRefs,
  incomingRefs,
  domainsSorted,
  entitiesInDomain,
  entityDegree,
} from './selectors.js';

type Direction = 'up' | 'down' | 'left' | 'right';

/** Target angle (in radians, screen coords with y-down) for each direction. */
const DIRECTION_ANGLE: Record<Direction, number> = {
  right: 0,
  down: Math.PI / 2,
  left: Math.PI,
  up: -Math.PI / 2,
};

/**
 * Walk to the neighbour of `focusedId` whose angle from the focal node best
 * matches `direction`. Falls back to the angularly-closest neighbour when no
 * neighbour sits in that quadrant. Returns undefined when the focus has no
 * neighbours at all (or doesn't exist).
 */
export function walkNeighborByDirection(
  manifest: AtlasManifest,
  layout: LayoutCoords,
  focusedId: string,
  direction: Direction,
): string | undefined {
  const focus = entityById(manifest, focusedId);
  if (!focus) return undefined;

  const neighbourIds = new Set<string>();
  for (const r of outgoingRefs(manifest, focusedId)) neighbourIds.add(r.to);
  for (const r of incomingRefs(manifest, focusedId)) neighbourIds.add(r.from);
  if (neighbourIds.size === 0) return undefined;

  const focal = layout[focusedId] ?? { x: 0, y: 0 };
  const target = DIRECTION_ANGLE[direction];

  let best: { id: string; distance: number } | null = null;
  for (const id of neighbourIds) {
    const coord = layout[id];
    if (!coord) continue;
    const dx = coord.x - focal.x;
    const dy = coord.y - focal.y;
    if (dx === 0 && dy === 0) continue;
    const angle = Math.atan2(dy, dx);
    const distance = angularDistance(angle, target);
    if (!best || distance < best.distance) {
      best = { id, distance };
    }
  }
  // No positioned neighbours → fall back to the first neighbour by id so the
  // keyboard still moves *somewhere* (deterministic for tests).
  if (!best) {
    const sorted = [...neighbourIds].sort();
    return sorted[0];
  }
  return best.id;
}

/** Smallest signed angular distance between two angles, range [0, π]. */
function angularDistance(a: number, b: number): number {
  const diff = Math.abs(a - b) % (2 * Math.PI);
  return diff > Math.PI ? 2 * Math.PI - diff : diff;
}

/**
 * Pick the natural "hub" of a domain — the entity with the highest reference
 * degree, breaking ties alphabetically. Returns undefined for an unknown
 * domain or one with no entities.
 */
export function pickHubEntityForDomain(
  manifest: AtlasManifest,
  domainId: string,
): string | undefined {
  const entities = entitiesInDomain(manifest, domainId);
  if (entities.length === 0) return undefined;
  let best: { id: string; degree: number; name: string } | null = null;
  for (const e of entities) {
    const { total } = entityDegree(manifest, e.id);
    if (
      !best ||
      total > best.degree ||
      (total === best.degree && e.name.localeCompare(best.name) < 0)
    ) {
      best = { id: e.id, degree: total, name: e.name };
    }
  }
  return best?.id;
}

/**
 * Map a domain index (0-based, in `domainsSorted` order) to the hub entity
 * of that domain. Used by the `1`–`9` keyboard shortcuts.
 */
export function entityForDomainIndex(
  manifest: AtlasManifest,
  index: number,
): string | undefined {
  if (index < 0) return undefined;
  const domains = domainsSorted(manifest);
  const domain = domains[index];
  if (!domain) return undefined;
  return pickHubEntityForDomain(manifest, domain.id);
}
